// src/oura/components/newscan/steps/WebsiteStep.tsx
//
// Step 1 of a new scan: "Which business?".
//
// One website field (the shared normaliser and the old modal's two warnings:
// a pasted page means the whole site, and a free Wix address keeps its path).
// "Read the site" first asks two quick questions, then reads:
// - Does a business own this site already? Then it routes by its stage
//   (JOURNEY-SPEC v1.1 §2.6), never to a second business: a scanned
//   prospect opens its page, a signed client still onboarding opens
//   onboarding (its first AI check is drafted there), a client past its
//   first check edits its questions. A prospect with no usable scan goes on
//   to the read, as any site does.
// - Is a draft for this site open? A card offers Continue it / Start over
//   (Start over discards it after a confirm row in the foot, then reads).
// - Otherwise the read runs, shown as a list of the pages being read that
//   fills in place (check / spinner / dot) inside a block that keeps its
//   height when the results land. A good read moves on to Business by itself;
//   a thin one says so and waits for Continue.
// Under the field, while nothing is being read: the open drafts, each with
// Continue, so a draft saved on Close is found without retyping its site.
// Under it, always, the optional Search Console card.
//
// Checking for an existing client before reading the site is deliberate: a
// client's site is never read into a new form, so the Business step can
// never be reached with someone else's details, and an existing client costs
// no read.

import { ActivityIcon, ArrowRightIcon, Building2Icon, CheckIcon, FilePenLineIcon, InfoIcon, Loader2Icon, PenLineIcon } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { ScanDraft } from "@service/aeo/scanDraftTypes";
import { discardScanDraft, findBusinessBySite, findOpenScanDraft, listStartedScanDrafts, type SiteOwnerStage } from "@service/aeo/scanDraft";
import { FIRST_CHECK_MINUTES } from "@oura/onboarding/derive";
import { onboardingHref } from "@oura/newscan/firstCheckLinks";
import { shortDate } from "@oura/components/newscan/brandReview";
import { cn } from "@oura/lib/cn";
import { DrawerNotice, Surface, SURFACE, TYPE } from "@oura/ui";
import { OURA_ROOT } from "@oura/nav";
import { agoText } from "@oura/docs/format";
import { Field, Refusal } from "@oura/components/onboarding/fields";
import { inputClass } from "@oura/onboarding/messageText";
import { StepHead } from "@oura/components/newscan/StepHead";
import { DecisionCard } from "@oura/components/newscan/DecisionCard";
import { Muted } from "@oura/components/newscan/Muted";
import { GscCard } from "@oura/components/newscan/GscCard";
import { OpenDraftRows } from "@oura/components/newscan/OpenDraftList";
import { useOpenScanDrafts } from "@oura/newscan/useOpenScanDrafts";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import { FRAME, OPEN_DRAFTS, READ_PAGES } from "@oura/newscan/copy";
import { droppedPath, hostOf, isPathAddressedHost, normalizeDomain } from "@oura/newscan/flowSteps";

const COPY = {
    title: "Which business?",
    sentence: "Paste the website. Oura reads it in about 8 seconds and suggests the details for you to check.",
    label: "Website",
    placeholder: "clinic.com.my",
    invalid: "Enter the website, like clinic.com.my.",
    wholeSite: (site: string, path: string) => `Reads ${site}, the whole site, not just ${path}.`,
    wixA: "A free Wix address puts the site on a path, so Oura sends the whole address: ",
    wixB: ". If the site has its own domain, use that instead.",
    note: "About 8 seconds. Free.",
    read: "Read the site",
    reading: "Reading",
    readAgain: "Read again",
    continue: "Continue",
    checking: "Checking the site",
    readingHead: (site: string) => `Reading ${site}`,
    readHead: (site: string) => `Read ${site}`,
    progress: (done: number, of: number) => `${done} of ${of}`,
    pagesRead: (n: number) => `${n} ${n === 1 ? "page" : "pages"}`,
    pageDone: "read",
    pageNow: "reading now",
    saidRead: (site: string) => `Read ${site}.`,
    /** What the read saw that decides found by place or by what it sells: "a cart · no address customers visit". */
    signals: (signals: readonly string[]) => `Found ${signals.join(" · ")}.`,
    low: "We could not read much from this site. Fill the details on the next step.",
    // The stage gate (JOURNEY-SPEC §2.6). Final copy.
    prospectTitle: (name: string) => `${name} is already in Oura`,
    prospectDetail: (day: string | null) => (day ? `Scanned ${day}.` : "Scanned before."),
    openPage: "Open its page",
    signedTitle: (name: string, day: string | null) => (day ? `${name} signed on ${day}` : `${name} has signed`),
    signedDetail: "Its first AI check is drafted from the pitch and the form.",
    openOnboarding: "Open onboarding",
    clientTitle: (name: string) => `${name} is a client`,
    clientDetail: "Its 50 are frozen.",
    editIts: "Edit its questions",
    different: "Use a different website",
    scanningTitle: (name: string) => `A scan for ${name} is running`,
    scanningDetail: (ago: string | null, minutes: number) =>
        `${ago ? `Started ${ago}. ` : ""}Its results land on the business page in about ${minutes} minutes. Start a new draft after it finishes.`,
    openBusiness: "Open the business",
    draftTitle: "A draft for this site is open",
    draftDetail: (ago: string | null) => `${ago ? `Started ${ago}. ` : ""}Continue it, or discard it and read the site again.`,
    continueIt: "Continue it",
    startOver: "Start over",
    discardAsk: "Start over? The open draft and its changes are discarded.",
    discardVerb: "Start over",
} as const;

type Phase = "idle" | "checking" | "reading" | "settle" | "leaving";

type Gate =
    | { kind: "scanning"; site: string; businessId: string | null; businessName: string; startedAt: string; minutes: number }
    | { kind: "owner"; stage: SiteOwnerStage; site: string; businessId: string; businessName: string; scannedAt: string | null; signedAt: string | null }
    | { kind: "draft"; site: string; draft: ScanDraft };

/** A good read rests this long with every page ticked before Business opens. */
const SETTLE_MS = 450;

export default function WebsiteStep() {
    const flow = useScanFlow();
    const { site, foot, goTo, openDraft, setDomain, readSite } = flow;
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);
    const listHeadId = useId();
    const draftsHeadId = useId();
    // Drafts saved on Close: picked up here without retyping their site.
    const openDrafts = useOpenScanDrafts("new_scan");

    const [phase, setPhase] = useState<Phase>("idle");
    const [gate, setGate] = useState<Gate | null>(null);
    const [showInvalid, setShowInvalid] = useState(false);
    // The site the read on screen belongs to. A read restored with the tab is the typed site's.
    const [readFor, setReadFor] = useState<string | null>(site.detected ? site.normalized : null);
    const [confirmOver, setConfirmOver] = useState(false);
    const [discarding, setDiscarding] = useState(false);
    const [discardError, setDiscardError] = useState<string | null>(null);

    const runSeq = useRef(0);
    useEffect(
        () => () => {
            // Leaving the step: answers still in flight are dropped.
            runSeq.current += 1;
        },
        [],
    );

    const normalized = site.normalized;
    const busy = phase !== "idle";
    const hasRead = Boolean(site.detected) && readFor === normalized && !site.reading;
    const low = hasRead && site.detected?.confidence === "low";

    /* ---- the read ---- */
    const read = useCallback(
        async (target: string, seq: number) => {
            setPhase("reading");
            setReadFor(target);
            await readSite();
            if (seq !== runSeq.current) return;
            setPhase("settle");
        },
        [readSite],
    );

    const run = useCallback(async () => {
        if (busy) return;
        const target = normalized;
        if (!target) {
            setShowInvalid(true);
            inputRef.current?.focus();
            return;
        }
        const seq = ++runSeq.current;
        setGate(null);
        setConfirmOver(false);
        setDiscardError(null);
        setPhase("checking");
        const [client, open, started] = await Promise.all([
            findBusinessBySite(target),
            findOpenScanDraft({ domain: target }),
            listStartedScanDrafts(),
        ]);
        if (seq !== runSeq.current) return;
        // A failed check never blocks: the server refuses a second business on create anyway.
        // The whole site identity, path included: two Wix sites under one free host are two businesses (Jack, #4286).
        const running = started.ok ? started.data.find((s) => normalizeDomain(s.domain) === normalizeDomain(target)) : undefined;
        if (running) {
            setGate({
                kind: "scanning",
                site: target,
                businessId: running.businessId,
                businessName: running.businessName || target,
                startedAt: running.startedAt,
                // One duration everywhere (JOURNEY-SPEC §2.5).
                minutes: FIRST_CHECK_MINUTES,
            });
            setPhase("idle");
            return;
        }
        // A prospect with no usable scan goes on, as any site does: the draft is made on that business (H3 rule 4).
        const owner = client.ok ? client.data : null;
        if (owner && (owner.stage !== "prospect" || owner.scannedAt)) {
            setGate({ kind: "owner", site: target, ...owner });
            setPhase("idle");
            return;
        }
        if (open.ok && open.data) {
            setGate({ kind: "draft", site: target, draft: open.data });
            setPhase("idle");
            return;
        }
        await read(target, seq);
    }, [busy, normalized, read]);

    // Once the read has landed: a good one moves on, a thin one or a failure stays here.
    useEffect(() => {
        if (phase !== "settle" || site.reading) return;
        if (site.error || !site.detected || site.detected.confidence === "low") {
            setPhase("idle");
            return;
        }
        setPhase("leaving");
    }, [phase, site.reading, site.error, site.detected]);

    useEffect(() => {
        if (phase !== "leaving") return;
        const t = window.setTimeout(() => {
            setPhase("idle");
            goTo("business");
        }, SETTLE_MS);
        return () => window.clearTimeout(t);
    }, [phase, goTo]);

    // `?site=` (the hub's "Draft the 50" on a never-scanned prospect): the field is
    // filled and read once, as if typed and pressed, so the flow lands on Business.
    const [params, setParams] = useSearchParams();
    const [askedSite] = useState(() => (flow.draftId ? null : params.get("site")?.trim() || null));
    const askedNormalized = askedSite ? normalizeDomain(askedSite) : "";
    const askedRun = useRef(false);
    useEffect(() => {
        if (askedSite) setDomain(askedSite);
        // Mount only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        // Not before the flow has adopted the asked site: a restored earlier site is not it.
        if (!askedSite || !askedNormalized || askedRun.current || normalized !== askedNormalized || busy) return;
        askedRun.current = true;
        setParams(
            (was) => {
                const next = new URLSearchParams(was);
                next.delete("site");
                return next;
            },
            { replace: true },
        );
        void run();
    }, [askedSite, askedNormalized, normalized, busy, run, setParams]);

    /* ---- the cards ---- */
    const differentSite = useCallback(() => {
        runSeq.current += 1;
        setGate(null);
        setConfirmOver(false);
        setDiscardError(null);
        setShowInvalid(false);
        setDomain("");
        requestAnimationFrame(() => inputRef.current?.focus());
    }, [setDomain]);

    const startOver = useCallback(async () => {
        if (gate?.kind !== "draft" || discarding) return;
        const seq = ++runSeq.current;
        setDiscarding(true);
        setDiscardError(null);
        // The draft's version, so a draft another person edited since is not thrown away blind (ourteam-backend#6080).
        const res = await discardScanDraft(gate.draft.id, gate.draft.version);
        setDiscarding(false);
        if (seq !== runSeq.current) return;
        setConfirmOver(false);
        if (!res.ok) {
            setDiscardError(res.kind === "network" ? FRAME.network : res.message);
            return;
        }
        setGate(null);
        await read(gate.site, seq);
    }, [gate, discarding, read]);

    // A card took the read's place: focus its first choice (Edit its questions / Continue it).
    // Its title is spoken through the status line below, since a focused button alone does not say it.
    const gateRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!gate) return;
        const raf = requestAnimationFrame(() => gateRef.current?.querySelector<HTMLElement>("button")?.focus());
        return () => cancelAnimationFrame(raf);
    }, [gate]);

    // Arriving on the step: the field takes focus, so keys typed straight away land in it.
    // Once, on the frame after mount (the step's fade has started). Not when a draft is open;
    // a card that opens later takes focus itself (above).
    const hasOpenDraft = Boolean(flow.draftId);
    useEffect(() => {
        if (hasOpenDraft) return;
        const raf = requestAnimationFrame(() => {
            if (gateRef.current?.childElementCount) return;
            inputRef.current?.focus({ preventScroll: true });
        });
        return () => cancelAnimationFrame(raf);
        // Mount only: later focus moves belong to the run, the reset and the cards.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ---- the field ---- */
    const onChange = (value: string) => {
        setShowInvalid(false);
        if (gate) {
            runSeq.current += 1;
            setGate(null);
            setConfirmOver(false);
            setDiscardError(null);
        }
        setDomain(value);
    };

    const primary = useCallback(() => {
        if (busy || gate) return;
        if (hasRead) goTo("business");
        else void run();
    }, [busy, gate, hasRead, goTo, run]);

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();
        primary();
    };

    /* ---- the foot ---- */
    useEffect(() => {
        if (confirmOver && gate?.kind === "draft") {
            foot.set({
                confirm: {
                    text: COPY.discardAsk,
                    verb: COPY.discardVerb,
                    busy: discarding,
                    onConfirm: () => void startOver(),
                    onCancel: () => setConfirmOver(false),
                },
            });
            return;
        }
        if (gate) {
            foot.set({ note: COPY.note });
            return;
        }
        if (busy) {
            foot.set({ note: COPY.note, primary: { label: COPY.reading, busy: true, onClick: () => undefined } });
            return;
        }
        if (hasRead) {
            foot.set({
                note: COPY.note,
                secondary: { label: COPY.readAgain, onClick: () => void run() },
                primary: { label: COPY.continue, icon: ArrowRightIcon, onClick: () => goTo("business") },
            });
            return;
        }
        foot.set({ note: COPY.note, primary: { label: COPY.read, icon: ArrowRightIcon, onClick: () => void run() } });
    }, [foot, confirmOver, gate, discarding, startOver, busy, hasRead, run, goTo]);

    /* ---- what the field says ---- */
    const typed = site.domain.trim();
    const invalid = showInvalid && !normalized ? COPY.invalid : null;
    const wix = normalized && isPathAddressedHost(hostOf(normalized)) && normalized.includes("/");
    const dropped = normalized && !wix ? droppedPath(site.domain) : null;

    /* ---- the progress list ---- */
    const showList = !gate && (busy || site.reading || hasRead);
    const listSite = readFor ?? normalized;
    const pages: readonly string[] = hasRead && site.detected ? site.detected.pages : READ_PAGES;
    const reading = phase === "checking" || phase === "reading" || site.reading;
    const doneCount = hasRead ? pages.length : phase === "checking" ? 0 : Math.min(site.readDone, pages.length);
    const signals = hasRead ? (site.detected?.signals ?? []).map((x) => x.trim()).filter(Boolean) : [];

    return (
        <div>
            <StepHead title={COPY.title} sentence={COPY.sentence} />
            {/* The column every step uses; only the field keeps a reading width. */}
            <div className="space-y-5">
                <form noValidate onSubmit={onSubmit} className="max-w-[640px] space-y-3">
                    <Field label={COPY.label} error={invalid} hint={dropped ? COPY.wholeSite(normalized, dropped) : undefined}>
                        {({ id, describedBy }) => (
                            <input
                                ref={inputRef}
                                id={id}
                                aria-describedby={describedBy}
                                aria-invalid={Boolean(invalid)}
                                className={inputClass(invalid)}
                                type="text"
                                inputMode="url"
                                autoComplete="off"
                                autoCapitalize="off"
                                autoCorrect="off"
                                spellCheck={false}
                                enterKeyHint="go"
                                placeholder={COPY.placeholder}
                                value={site.domain}
                                readOnly={busy}
                                onChange={(e) => onChange(e.target.value)}
                                onBlur={() => {
                                    if (typed && !normalized) setShowInvalid(true);
                                }}
                            />
                        )}
                    </Field>
                    {wix && (
                        <DrawerNotice>
                            {COPY.wixA}
                            <span className="font-medium">{normalized}</span>
                            {COPY.wixB}
                        </DrawerNotice>
                    )}
                    {site.error && !busy && <Refusal>{site.error}</Refusal>}
                </form>

                {openDrafts && openDrafts.length > 0 && !gate && !busy && !hasRead && (
                    <Surface className="gap-1">
                        <h2 id={draftsHeadId} className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>
                            {OPEN_DRAFTS.title}
                        </h2>
                        <div role="group" aria-labelledby={draftsHeadId}>
                            <OpenDraftRows drafts={openDrafts} onContinue={openDraft} />
                        </div>
                    </Surface>
                )}

                {showList && (
                    <section
                        aria-labelledby={listHeadId}
                        className={cn(SURFACE.block, "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-200")}
                    >
                        <h2 id={listHeadId} className={cn(TYPE.tb, "m-0 min-w-0 truncate font-semibold text-gray-900")}>
                            {phase === "checking" ? COPY.checking : reading ? COPY.readingHead(listSite) : COPY.readHead(listSite)}
                        </h2>
                        <Muted as="p" className="m-0 mt-0.5 tabular-nums">
                            {hasRead ? COPY.pagesRead(pages.length) : COPY.progress(doneCount, pages.length)}
                        </Muted>
                        <ol className="list-plain m-0 mt-3 space-y-2 p-0">
                            {pages.map((page, i) => {
                                const done = i < doneCount;
                                const now = !done && i === doneCount && reading;
                                return (
                                    <li
                                        key={i}
                                        className={cn(
                                            TYPE.tb,
                                            "flex min-w-0 items-center gap-2.5 motion-safe:transition-colors motion-safe:duration-200",
                                            done || now ? "text-gray-900" : "text-gray-400",
                                        )}
                                    >
                                        <span aria-hidden className="grid h-4 w-4 shrink-0 place-items-center">
                                            {done && low ? (
                                                // A thin read: the pages were opened, but little was found. A grey dot, never a tick
                                                // that reads as "read well" (Angel, 15 Oct).
                                                <span key="thin" className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                                            ) : done ? (
                                                <CheckIcon
                                                    key="done"
                                                    size={14}
                                                    strokeWidth={2.5}
                                                    className="text-emerald-600 motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:fade-in motion-safe:duration-200"
                                                />
                                            ) : now ? (
                                                <Loader2Icon key="now" size={14} className="text-blue-600 motion-safe:animate-spin" />
                                            ) : (
                                                <span key="later" className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                                            )}
                                        </span>
                                        <span className="min-w-0 truncate">{page}</span>
                                        <span className="sr-only">{done ? `, ${COPY.pageDone}` : now ? `, ${COPY.pageNow}` : ""}</span>
                                    </li>
                                );
                            })}
                        </ol>
                        {signals.length > 0 && (
                            <p className={cn(TYPE.tc, "m-0 mt-4 flex items-start gap-2 border-t border-gray-100 pt-3 text-gray-600")}>
                                <InfoIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-gray-400" />
                                <span className="min-w-0 break-words">{COPY.signals(signals)}</span>
                            </p>
                        )}
                        {low && (
                            <p className={cn(TYPE.tc, "m-0 mt-4 flex items-start gap-2 border-t border-gray-100 pt-3 text-gray-600")}>
                                <InfoIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-gray-400" />
                                <span className="min-w-0 break-words">
                                    {site.detected?.reason ? `${site.detected.reason} ` : ""}
                                    {COPY.low}
                                </span>
                            </p>
                        )}
                        <span role="status" className="sr-only">
                            {hasRead ? `${COPY.saidRead(listSite)}${signals.length ? ` ${COPY.signals(signals)}` : ""}` : ""}
                        </span>
                    </section>
                )}

                <span role="status" className="sr-only">
                    {gate
                        ? gate.kind === "scanning"
                            ? COPY.scanningTitle(gate.businessName)
                            : gate.kind === "owner"
                              ? ownerTitle(gate)
                              : COPY.draftTitle
                        : ""}
                </span>

                <div ref={gateRef} data-gate="" className="empty:hidden">
                    {gate?.kind === "scanning" && (
                        <DecisionCard
                            severity="decide"
                            icon={ActivityIcon}
                            title={COPY.scanningTitle(gate.businessName)}
                            detail={COPY.scanningDetail(agoText(gate.startedAt), gate.minutes)}
                            className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
                            actions={[
                                ...(gate.businessId
                                    ? [
                                          {
                                              key: "open",
                                              label: COPY.openBusiness,
                                              kind: "primary" as const,
                                              icon: ArrowRightIcon,
                                              onClick: () => navigate(`${OURA_ROOT}/businesses/${encodeURIComponent(gate.businessId ?? "")}`),
                                          },
                                      ]
                                    : []),
                                { key: "different", label: COPY.different, kind: "text", onClick: differentSite },
                            ]}
                        />
                    )}

                    {gate?.kind === "owner" && (
                        <DecisionCard
                            severity="decide"
                            icon={gate.stage === "client" ? PenLineIcon : Building2Icon}
                            title={ownerTitle(gate)}
                            detail={
                                gate.stage === "prospect"
                                    ? COPY.prospectDetail(shortDate(gate.scannedAt))
                                    : gate.stage === "onboarding"
                                      ? COPY.signedDetail
                                      : COPY.clientDetail
                            }
                            className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
                            actions={[
                                {
                                    key: "open",
                                    label: gate.stage === "prospect" ? COPY.openPage : gate.stage === "onboarding" ? COPY.openOnboarding : COPY.editIts,
                                    kind: "primary",
                                    icon: ArrowRightIcon,
                                    onClick: () =>
                                        navigate(
                                            gate.stage === "prospect"
                                                ? `${OURA_ROOT}/businesses/${encodeURIComponent(gate.businessId)}`
                                                : gate.stage === "onboarding"
                                                  ? onboardingHref(gate.businessId)
                                                  : `${OURA_ROOT}/businesses/${encodeURIComponent(gate.businessId)}/questions/draft`,
                                        ),
                                },
                                { key: "different", label: COPY.different, kind: "text", onClick: differentSite },
                            ]}
                        />
                    )}

                    {gate?.kind === "draft" && (
                        <DecisionCard
                            severity="decide"
                            icon={FilePenLineIcon}
                            title={COPY.draftTitle}
                            detail={COPY.draftDetail(agoText(gate.draft.createdAt))}
                            className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
                            disabled={discarding || confirmOver}
                            actions={[
                                { key: "continue", label: COPY.continueIt, kind: "primary", onClick: () => openDraft(gate.draft.id) },
                                { key: "over", label: COPY.startOver, kind: "text", onClick: () => setConfirmOver(true) },
                            ]}
                        >
                            {discardError && <Refusal>{discardError}</Refusal>}
                        </DecisionCard>
                    )}
                </div>

                {!gate && <GscCard />}
            </div>
        </div>
    );
}

/** The stage gate's title: what this site already is in Oura. */
function ownerTitle(gate: Extract<Gate, { kind: "owner" }>): string {
    if (gate.stage === "prospect") return COPY.prospectTitle(gate.businessName);
    if (gate.stage === "onboarding") return COPY.signedTitle(gate.businessName, shortDate(gate.signedAt));
    return COPY.clientTitle(gate.businessName);
}
